/**
 * TopoJSON compression levels for optimal map rendering performance.
 * These levels are designed to balance detail preservation with rendering performance
 * based on the current map scale/zoom level.
 */

export interface CompressionLevel {
  /** Human-readable name for the compression level */
  readonly name: ECompressionLevel;
  readonly label: string;
  /** Simplification threshold for topology compression (lower = more detail) */
  readonly threshold: number;
  /** Description of when this level should be used */
  readonly description: string;
  /** Minimum scale this level is optimized for */
  readonly minScale: number;
  /** Maximum scale this level is optimized for */
  readonly maxScale: number;
}
export enum ECompressionLevel {
  ULTRA_HIGH = 'ULTRA_HIGH',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  MINIMAL = 'MINIMAL',
}
/**
 * Pre-defined compression levels for instant usage.
 * Ordered from highest detail to lowest detail.
 */
export const COMPRESSION_LEVELS: Record<ECompressionLevel, CompressionLevel> = {
  /** Ultra-high detail for very high zoom levels (scale > 2000) */
  ULTRA_HIGH: {
    name: ECompressionLevel.ULTRA_HIGH,
    label: 'Ultra High',
    threshold: 0.0005,
    description: 'Maximum detail for close-up views',
    minScale: 14000,
    maxScale: Number.MAX_SAFE_INTEGER,
  },

  /** High detail for medium-high zoom levels (scale 1000-2000) */
  HIGH: {
    name: ECompressionLevel.HIGH,
    label: 'High',
    threshold: 0.005,
    description: 'High detail for detailed regional views',
    minScale: 2000,
    maxScale: 14000,
  },

  /** Medium detail for medium zoom levels (scale 500-1000) */
  MEDIUM: {
    name: ECompressionLevel.MEDIUM,
    label: 'Medium',
    threshold: 0.01,
    description: 'Balanced detail for standard views',
    minScale: 500,
    maxScale: 2000,
  },

  /** Low detail for low zoom levels (scale 200-500) */
  LOW: {
    name: ECompressionLevel.LOW,
    label: 'Low',
    threshold: 0.05,
    description: 'Reduced detail for wide area views',
    minScale: 200,
    maxScale: 500,
  },

  /** Minimal detail for very low zoom levels (scale < 200) */
  MINIMAL: {
    name: ECompressionLevel.MINIMAL,
    label: 'Minimal',
    threshold: 0.05,
    description: 'Minimal detail for global overview',
    minScale: 0,
    maxScale: 200,
  },
};

/**
 * Array of compression levels ordered by scale (highest to lowest).
 * Used for efficient scale-based lookup.
 */
export const COMPRESSION_LEVELS_BY_SCALE: readonly CompressionLevel[] = [
  COMPRESSION_LEVELS.ULTRA_HIGH,
  COMPRESSION_LEVELS.HIGH,
  COMPRESSION_LEVELS.MEDIUM,
  COMPRESSION_LEVELS.LOW,
  COMPRESSION_LEVELS.MINIMAL,
] as const;

/**
 * Determines the optimal compression level based on the current map scale.
 * Uses binary search for efficient lookup.
 *
 * @param scale - Current map projection scale
 * @returns The optimal compression level for the given scale
 */
export const getCompressionLevelForScale = (
  scale: number,
): CompressionLevel => {
  // Find the first level where scale >= minScale
  for (const level of COMPRESSION_LEVELS_BY_SCALE) {
    if (scale >= level.minScale) {
      return level;
    }
  }

  // Fallback to minimal compression for very low scales
  return COMPRESSION_LEVELS.MINIMAL;
};

/**
 * Get a compression level by name.
 * Useful for external configuration or user preferences.
 *
 * @param name - The name of the compression level
 * @returns The compression level configuration
 */
export const getCompressionLevelByName = (
  name: ECompressionLevel,
): CompressionLevel => COMPRESSION_LEVELS[name];
